import React, { useEffect, useMemo, useState } from 'react';
import JobCard from './JobCard';
import useFetch from '../../useFetch';
import { mapApiJob } from '../../utils/jobFormatters';
import EmptyState from '../Common/EmptyState';

const getVisiblePages = (currentPage, lastPage) => {
  if (lastPage <= 5) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  const start = Math.max(Math.min(currentPage - 2, lastPage - 4), 1);

  return Array.from({ length: 5 }, (_, index) => start + index);
};

const JobList = ({ filters = {}, page = 1, onPageChange, onSortChange }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState([]);
  const [perPage, setPerPage] = useState(10);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('per_page', perPage);
    params.set('page', page);

    if (filters.keyword) params.set('keyword', filters.keyword);
    if (filters.location) params.set('location', filters.location);
    if (filters.category) params.set('category', filters.category);
    if (filters.experience) params.set('experience', filters.experience);
    if (filters.salaryMin) params.set('salary_min', filters.salaryMin);
    if (filters.salaryMax) params.set('salary_max', filters.salaryMax);
    if (filters.jobTypes?.length > 0) params.set('job_types', filters.jobTypes.join(','));
    if (filters.datePosted) params.set('date_posted', filters.datePosted);
    if (filters.sort) params.set('sort', filters.sort);

    return process.env.REACT_APP_BASE_API_URL + '/jobs?' + params.toString();
  }, [filters, page, perPage]);

  const { response, isLoading, error: fetchError } = useFetch(apiUrl);

  useEffect(() => {
    setItems(response?.jobs?.data?.map(mapApiJob) ?? []);
    setMeta(response?.jobs?.meta ?? []);
  }, [response]);

  const handlePerPageChange = (e) => {
    const selectedPerPage = Number(e.target.value);

    setPerPage(selectedPerPage);
    onPageChange?.(1);
  };

  const changePage = (nextPage) => {
    const lastPage = meta?.last_page ?? 1;
    const safePage = Math.min(Math.max(nextPage, 1), lastPage);

    if (safePage !== meta?.current_page) {
      onPageChange?.(safePage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const shownCount = items.length;
  const totalCount = meta?.total ?? 0;
  const currentPage = meta?.current_page ?? page;
  const lastPage = meta?.last_page ?? 1;
  const showingFrom = shownCount > 0 ? ((currentPage - 1) * (meta?.per_page ?? perPage)) + 1 : 0;
  const showingTo = shownCount > 0 ? showingFrom + shownCount - 1 : 0;
  const visiblePages = getVisiblePages(currentPage, lastPage);

  return (
    <div className="ls-outer">
      <button type="button" className="theme-btn btn-style-two toggle-filters">Show Filters</button>

      {/* ls Switcher */}
      <div className="ls-switcher">
        <div className="showing-result">
          <div className="text">
            {isLoading ? 'Loading jobs' : <>Showing <strong>{showingFrom}-{showingTo}</strong> of <strong>{totalCount}</strong> jobs</>}
          </div>
        </div>
        <div className="sort-by">
          <select className="chosen-select" value={filters.sort ?? 'newest'} onChange={(e) => onSortChange?.(e.target.value)}>
            <option value="newest">Newest Jobs</option>
            <option value="relevance">Most Relevant</option>
          </select>

          <select className="chosen-select" value={perPage} onChange={handlePerPageChange}>
            <option value="10">Show 10</option>
            <option value="20">Show 20</option>
            <option value="30">Show 30</option>
            <option value="40">Show 40</option>
            <option value="50">Show 50</option>
          </select>
        </div>
      </div>

      {fetchError && <div className="alert alert-danger"><strong>{fetchError}</strong></div>}

      {isLoading && <h5 className="text-center my-4">Loading Data</h5>}

      {!fetchError && !isLoading && items.length === 0 && (
        <EmptyState
          icon="la la-search"
          title="No jobs matched your search"
          text="Try changing the keyword, location, salary, or category filters."
          actionText="Reset Search"
          actionTo="/jobs"
        />
      )}

      {!fetchError && !isLoading && items.map((job) => (
        <JobCard key={job.id} job={job} />
      ))}

      {!fetchError && !isLoading && lastPage > 1 && (
        <nav className="ls-pagination" aria-label="Job listings pagination">
          <ul>
            <li className="prev">
              <button type="button" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
                <i className="fa fa-arrow-left"></i>
              </button>
            </li>
            {visiblePages.map((pageNumber) => (
              <li key={pageNumber}>
                <button
                  type="button"
                  className={pageNumber === currentPage ? 'current-page' : ''}
                  onClick={() => changePage(pageNumber)}
                >
                  {pageNumber}
                </button>
              </li>
            ))}
            <li className="next">
              <button type="button" disabled={currentPage === lastPage} onClick={() => changePage(currentPage + 1)}>
                <i className="fa fa-arrow-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default JobList;
